import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { getInstanceUsage } from '../../../../services';
import { validateApiResponse } from '../../../../shared/utils/apiResponse';

/**
 * Per-instance usage: stats + daily call-volume series + capability breakdown +
 * consumers (CT-4193). `period` (7/30/90 days) sizes the series; changing it
 * re-fetches. Returns a reference `now` captured off-render at load so relative
 * last-used timestamps stay lint-pure. Race/unmount-safe like useInstanceLogs.
 */
export function useInstanceUsage(instanceId, period = 30) {
  const { t } = useTranslation('admin-studio');
  const [stats, setStats] = useState({});
  const [series, setSeries] = useState([]);
  const [capabilities, setCapabilities] = useState([]);
  const [consumers, setConsumers] = useState([]);
  const [now, setNow] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchIdRef = useRef(0);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    const fetchId = ++fetchIdRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const res = await getInstanceUsage(instanceId, { period });
      if (!mountedRef.current || fetchId !== fetchIdRef.current) return;
      const body = validateApiResponse(res, t('common.requestFailed')).body ?? {};
      setStats(body.stats ?? {});
      setSeries(body.series ?? []);
      setCapabilities(body.capabilities ?? []);
      setConsumers(body.consumers ?? []);
      setNow(Date.now());
    } catch (err) {
      if (!mountedRef.current || fetchId !== fetchIdRef.current) return;
      setError(err?.message ?? t('common.requestFailed'));
    } finally {
      if (mountedRef.current && fetchId === fetchIdRef.current) setIsLoading(false);
    }
  }, [instanceId, period, t]);

  useEffect(() => {
    load();
  }, [load]);

  return useMemo(
    () => ({ stats, series, capabilities, consumers, now, isLoading, error }),
    [stats, series, capabilities, consumers, now, isLoading, error],
  );
}
